import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Shield, FileCheck, AlertTriangle, CheckCircle, Globe, Clock, Umbrella, Scale } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import bannerShipping from "@/assets/banner-shipping.jpg";
import globalNetwork from "@/assets/global-network.jpg";

const Insurance = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const coverageTypes = [
    {
      icon: Globe,
      title: "All-Risk Coverage",
      description: "Comprehensive protection against all physical loss or damage during transit",
      features: ["Theft and pilferage", "Weather damage", "Accidents", "Fire and explosion"]
    },
    {
      icon: Shield,
      title: "Marine Insurance",
      description: "Specialized coverage for ocean freight including general average",
      features: ["Hull insurance", "Cargo insurance", "General average", "Salvage charges"]
    },
    {
      icon: Umbrella,
      title: "Warehouse Coverage",
      description: "Protection for goods stored in transit and warehouse facilities",
      features: ["Storage risks", "Handling damage", "Environmental hazards", "Security coverage"]
    },
    {
      icon: Scale,
      title: "Liability Insurance",
      description: "Coverage for freight forwarder and carrier liability",
      features: ["Errors & omissions", "Cargo liability", "Professional indemnity", "Third-party claims"]
    }
  ];

  const claimsProcess = [
    { step: "1", title: "Report", desc: "Notify us immediately of any loss or damage" },
    { step: "2", title: "Document", desc: "Gather photos, reports, and shipping documents" },
    { step: "3", title: "Submit", desc: "Complete claim form with supporting evidence" },
    { step: "4", title: "Review", desc: "Our team assesses the claim" },
    { step: "5", title: "Settlement", desc: "Receive fair and prompt compensation" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={bannerShipping} 
          alt="Cargo Insurance" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Insurance & Claims</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Protect your cargo with comprehensive insurance solutions. Expert claims handling when you need it most.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Get Insurance Quote
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Peace of Mind for Every Shipment</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  International shipping carries inherent risks. From rough seas to warehouse mishaps, your cargo faces numerous potential hazards. Our insurance solutions provide the protection you need.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  Working with leading insurers, we offer tailored coverage at competitive rates. And if the worst happens, our dedicated claims team ensures fast, fair settlements.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">€500M+</div>
                    <div className="text-sm text-muted-foreground">Cargo Insured Annually</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">98%</div>
                    <div className="text-sm text-muted-foreground">Claims Approval Rate</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">14 days</div>
                    <div className="text-sm text-muted-foreground">Average Settlement Time</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">A-rated</div>
                    <div className="text-sm text-muted-foreground">Insurance Partners</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={globalNetwork} 
                  alt="Global Insurance Coverage" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Coverage Types */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Insurance Coverage Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {coverageTypes.map((coverage, index) => {
                const Icon = coverage.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{coverage.title}</h3>
                      <p className="text-muted-foreground mb-6">{coverage.description}</p>
                      <div className="space-y-2">
                        {coverage.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Risk Assessment */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-8">Common Cargo Risks</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { risk: "Physical Damage", desc: "Crushing, breakage, denting during handling" },
                { risk: "Water Damage", desc: "Flooding, condensation, humidity exposure" },
                { risk: "Theft & Pilferage", desc: "Cargo theft at ports, terminals, or in transit" },
                { risk: "Temperature Excursion", desc: "Failure of refrigeration equipment" },
                { risk: "Contamination", desc: "Cross-contamination, infestation, odor damage" },
                { risk: "Non-Delivery", desc: "Lost shipments, misrouting, abandonment" },
                { risk: "Natural Disasters", desc: "Storms, earthquakes, flooding" },
                { risk: "Political Risk", desc: "War, strikes, civil unrest" }
              ].map((item, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <AlertTriangle className="h-6 w-6 text-amber-500 mb-2" />
                    <h3 className="font-semibold mb-1">{item.risk}</h3>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Claims Process */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Claims Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {claimsProcess.map((item, index) => (
                <div key={index} className="text-center animate-fade-in">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Coverage Levels */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Coverage Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  name: "Basic", 
                  coverage: "Named Perils", 
                  desc: "Coverage for specific listed risks",
                  features: ["Fire & explosion", "Vessel sinking", "Collision", "Jettison"],
                  price: "From 0.15%"
                },
                { 
                  name: "Standard", 
                  coverage: "All-Risk", 
                  desc: "Comprehensive coverage for most risks",
                  features: ["All basic perils", "Theft & pilferage", "Water damage", "Handling damage"],
                  price: "From 0.25%",
                  popular: true
                },
                { 
                  name: "Premium", 
                  coverage: "All-Risk Plus", 
                  desc: "Maximum protection with extended coverage",
                  features: ["All standard perils", "Delay coverage", "Rejection risk", "War & strikes"],
                  price: "From 0.40%"
                }
              ].map((plan, index) => (
                <Card key={index} className={`relative hover-scale animate-fade-in ${plan.popular ? 'border-2 border-primary' : ''}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  )}
                  <CardContent className="p-8 text-center">
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <p className="text-primary font-semibold mb-2">{plan.coverage}</p>
                    <p className="text-muted-foreground text-sm mb-6">{plan.desc}</p>
                    <div className="space-y-2 mb-6 text-left">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 text-primary mr-2" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-lg font-bold text-primary mb-4">{plan.price}</p>
                    <Button className="w-full" variant={plan.popular ? "default" : "outline"} onClick={() => setQuoteModalOpen(true)}>
                      Get Quote
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">
              * Rates shown are indicative and based on cargo value. Actual rates depend on commodity, route, and coverage selected.
            </p>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Insurance Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Clock, title: "Fast Claims", desc: "Dedicated claims team with average 14-day settlement for straightforward claims" },
                { icon: FileCheck, title: "Expert Advice", desc: "Insurance specialists who understand shipping risks and coverage needs" },
                { icon: Shield, title: "Strong Partners", desc: "A-rated insurers including Lloyd's syndicates and major global underwriters" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Protect Your Cargo Today</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Don't leave your shipments unprotected. Get a competitive insurance quote from our expert team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request Insurance Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Insurance Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="insurance" />
    </div>
  );
};

export default Insurance;
